/**
 * Norwegian Organization Number Validation
 *
 * Validates Norwegian organization numbers (organisasjonsnummer) using the MOD11 algorithm.
 * Organization numbers are 9-digit numbers where the last digit is a check digit.
 *
 * Algorithm:
 * 1. Multiply each of the first 8 digits by weights [3, 2, 7, 6, 5, 4, 3, 2]
 * 2. Sum all products
 * 3. Calculate remainder = sum % 11
 * 4. Calculate check digit:
 *    - If remainder = 0, check digit = 0
 *    - Otherwise, check digit = 11 - remainder
 *    - If check digit >= 10, the number is invalid
 * 5. Verify the 9th digit matches the calculated check digit
 *
 * @see https://www.brreg.no/ - Brønnøysund Register Centre (official registry)
 */

/**
 * Validates a Norwegian organization number using MOD11 algorithm
 *
 * @param {string} orgNumber - The organization number to validate (9 digits)
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * validateNorwegianOrgNumber('123456785') // returns true or false
 * validateNorwegianOrgNumber('123 456 785') // returns true or false (spaces are removed)
 */
export function validateNorwegianOrgNumber(orgNumber) {
    if (!orgNumber) {
        return false;
    }

    // Remove spaces and common separators
    const cleaned = orgNumber.replace(/[\s.-]/g, '');

    // Must be exactly 9 digits
    if (!/^\d{9}$/.test(cleaned)) {
        return false;
    }

    // Convert to array of digits
    const digits = cleaned.split('').map(Number);

    // Weights for MOD11 calculation (first 8 digits)
    const weights = [3, 2, 7, 6, 5, 4, 3, 2];

    // Calculate weighted sum
    let sum = 0;
    for (let i = 0; i < 8; i++) {
        sum += digits[i] * weights[i];
    }

    // Calculate check digit
    const remainder = sum % 11;
    let checkDigit;

    if (remainder === 0) {
        checkDigit = 0;
    } else {
        checkDigit = 11 - remainder;
    }

    // Invalid if check digit would be 10 or greater
    if (checkDigit >= 10) {
        return false;
    }

    // Verify check digit matches the 9th digit
    return checkDigit === digits[8];
}

/**
 * Formats a Norwegian organization number with spaces for display
 *
 * @param {string} orgNumber - The organization number (9 digits)
 * @returns {string} Formatted as "XXX XXX XXX" or original if invalid format
 *
 * @example
 * formatNorwegianOrgNumber('123456785') // returns '123 456 785'
 */
export function formatNorwegianOrgNumber(orgNumber) {
    if (!orgNumber) {
        return '';
    }

    const cleaned = orgNumber.replace(/[\s.-]/g, '');

    if (!/^\d{9}$/.test(cleaned)) {
        return orgNumber; // Return original if not valid format
    }

    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)}`;
}
